'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

// Platform Button — sharp-cornered, semantic-token-driven, separate from
// the marketing Button (which hardcodes brand-CTA pink + pill shape +
// scale-bounce hovers). Used inside /platform/app dashboard surfaces.

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    '[&_svg]:shrink-0 [&_svg]:pointer-events-none',
  ].join(' '),
  {
    variants: {
      variant: {
        // Fills use the AA tokens (--primary-fill / --cta-fill), not
        // --primary/--cta, so white labels meet 4.5:1 (see CLAUDE.md §7).
        primary: 'bg-[var(--primary-fill)] text-white hover:bg-[#006ba1]',
        cta: 'bg-[var(--cta-fill)] text-white hover:bg-[#c3086e]',
        gradient:
          'text-white bg-gradient-to-r from-[#0077b3] to-[#0077b3]/85 hover:opacity-90',
        secondary:
          'border border-border bg-transparent text-foreground hover:bg-[var(--muted)]',
        ghost: 'bg-transparent text-muted-foreground hover:bg-[var(--muted)]',
        destructive:
          'bg-transparent text-[var(--destructive)] hover:bg-[color-mix(in_srgb,var(--destructive)_10%,transparent)]',
      },
      size: {
        xs: 'h-7 px-2 text-[11px] [&_svg]:size-3',
        sm: 'h-8 px-3 text-xs [&_svg]:size-3.5',
        md: 'h-9 px-4 text-sm [&_svg]:size-4',
        lg: 'h-10 px-5 text-sm [&_svg]:size-4',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      rounded: 'none',
      fullWidth: false,
    },
  }
)

type IconInput = React.ComponentType<{ className?: string }> | React.ReactNode

function renderIcon(icon: IconInput | undefined, key: string) {
  if (!icon) return null
  // Lucide icons are forwardRef objects (`typeof === 'object'`), not
  // functions — so we discriminate by whether it's already a ReactElement.
  // If yes, render as-is. If no, it's a component reference to instantiate.
  if (React.isValidElement(icon)) {
    return <React.Fragment key={key}>{icon}</React.Fragment>
  }
  const IconComponent = icon as React.ComponentType<{ className?: string }>
  return <IconComponent key={key} />
}

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  loadingText?: string
  success?: boolean
  successText?: string
  leadingIcon?: IconInput
  trailingIcon?: IconInput
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      fullWidth,
      loading = false,
      loadingText,
      success = false,
      successText,
      leadingIcon,
      trailingIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isInert = disabled || loading || success

    const content = (() => {
      if (loading) {
        return (
          <>
            <Loader2 className='animate-spin' />
            {loadingText ?? children}
          </>
        )
      }
      if (success) {
        return (
          <>
            <Check />
            {successText ?? children}
          </>
        )
      }
      return (
        <>
          {renderIcon(leadingIcon, 'leading')}
          {children}
          {renderIcon(trailingIcon, 'trailing')}
        </>
      )
    })()

    return (
      <button
        ref={ref}
        disabled={isInert}
        aria-busy={loading || undefined}
        aria-live={success ? 'polite' : undefined}
        aria-atomic={success ? 'true' : undefined}
        className={cn(
          buttonVariants({ variant, size, rounded, fullWidth }),
          loading && 'cursor-wait',
          className
        )}
        {...props}
      >
        {content}
      </button>
    )
  }
)

Button.displayName = 'Button'

// Hook for the success-flash pattern used by Share / Copy buttons. Returns
// a controlled `success` boolean and a `trigger` function that flips it
// on for `durationMs` then auto-resets. Replaces the legacy pattern of
// mutating `btn.textContent` directly in event handlers.
export function useSuccessFlash(durationMs = 2000) {
  const [success, setSuccess] = React.useState(false)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const trigger = React.useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setSuccess(true)
    timeoutRef.current = setTimeout(() => {
      setSuccess(false)
      timeoutRef.current = null
    }, durationMs)
  }, [durationMs])

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return { success, trigger }
}
